import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { BusinessDocumentService } from '@/services/business-document.service';
import {
  BusinessDocument,
  BusinessDocumentType,
  BusinessDocumentStatus,
  BusinessDocumentTypeLabels,
  BusinessDocumentStatusLabels,
} from '@/models/business-document.model';
import { format } from 'date-fns';
import { Button, Card, Badge, LoadingSpinner, EmptyState, SectionHeader, FormInput, FormSelect } from '@/components/common';
import { FileText, Plus, Search, Filter, Download, Trash2, Edit, Calendar, AlertTriangle, CheckCircle2 } from 'lucide-react';

export const BusinessDocuments: React.FC = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<BusinessDocumentType | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<BusinessDocumentStatus | 'all'>('all');

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['businessDocuments', filterType, filterStatus],
    queryFn: async () => {
      let docs = await BusinessDocumentService.getAllDocuments();
      
      if (filterType !== 'all') {
        docs = docs.filter(d => d.type === filterType);
      }
      
      if (filterStatus !== 'all') {
        docs = docs.filter(d => d.status === filterStatus);
      }
      
      if (searchQuery.trim()) {
        docs = await BusinessDocumentService.searchDocuments(searchQuery);
      }
      
      return docs;
    },
    enabled: !!profile,
  });

  const { data: _expiringDocuments = [] } = useQuery({
    queryKey: ['businessDocumentsExpiring'],
    queryFn: () => BusinessDocumentService.getExpiringDocuments(30),
    enabled: !!profile,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await BusinessDocumentService.deleteDocument(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businessDocuments'] });
    },
  });

  const filteredDocuments = documents.filter(doc => {
    if (searchQuery && !doc.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !doc.documentNumber?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  const getStatusVariant = (status: BusinessDocumentStatus): "success" | "warning" | "danger" | "gray" => {
    switch (status) {
      case BusinessDocumentStatus.active:
        return 'success';
      case BusinessDocumentStatus.expired:
        return 'danger';
      case BusinessDocumentStatus.pendingRenewal:
        return 'warning';
      case BusinessDocumentStatus.archived:
        return 'gray';
    }
  };

  const isExpiringSoon = (expirationDate?: Date) => {
    if (!expirationDate) return false;
    const daysUntilExpiry = Math.ceil((expirationDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
  };

  const isExpired = (expirationDate?: Date) => {
    if (!expirationDate) return false;
    return expirationDate < new Date();
  };

  // Stats
  const totalDocuments = documents.length;
  const activeDocuments = documents.filter(d => d.status === BusinessDocumentStatus.active).length;
  const expiredDocuments = documents.filter(d => isExpired(d.expirationDate)).length;
  const expiringSoonCount = documents.filter(d => isExpiringSoon(d.expirationDate)).length;

  if (isLoading) {
    return <LoadingSpinner text="Loading business documents..." fullScreen />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SectionHeader
        title="Business Documents"
        description="Manage official business registration documents, licenses, and certifications"
        action={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/business-documents/new')}
          >
            Add Document
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600 mb-1">Total Documents</p>
              <p className="text-3xl font-bold text-blue-900">{totalDocuments}</p>
            </div>
            <FileText className="w-12 h-12 text-blue-400 opacity-50" />
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600 mb-1">Active</p>
              <p className="text-3xl font-bold text-green-900">{activeDocuments}</p>
            </div>
            <CheckCircle2 className="w-12 h-12 text-green-400 opacity-50" />
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-600 mb-1">Expiring Soon</p>
              <p className="text-3xl font-bold text-yellow-900">{expiringSoonCount}</p>
            </div>
            <AlertTriangle className="w-12 h-12 text-yellow-400 opacity-50" />
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-red-600 mb-1">Expired</p>
              <p className="text-3xl font-bold text-red-900">{expiredDocuments}</p>
            </div>
            <AlertTriangle className="w-12 h-12 text-red-400 opacity-50" />
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <FormInput
            label="Search"
            icon={Search}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or number..."
          />
          <FormSelect
            label="Document Type"
            icon={FileText}
            options={[
              { value: 'all', label: 'All Types' },
              ...Object.entries(BusinessDocumentTypeLabels).map(([value, label]) => ({
                value,
                label,
              })),
            ]}
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as BusinessDocumentType | 'all')}
          />
          <FormSelect
            label="Status"
            icon={Filter}
            options={[
              { value: 'all', label: 'All Statuses' },
              ...Object.entries(BusinessDocumentStatusLabels).map(([value, label]) => ({
                value,
                label,
              })),
            ]}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as BusinessDocumentStatus | 'all')}
          />
        </div>
      </Card>

      {/* Documents List */}
      <Card padding="none">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Documents ({filteredDocuments.length})
          </h3>
        </div>
        {filteredDocuments.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No documents found"
            description={
              searchQuery || filterType !== 'all' || filterStatus !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by adding your first business document'
            }
            action={
              !searchQuery && filterType === 'all' && filterStatus === 'all' && (
                <Button
                  variant="primary"
                  icon={Plus}
                  onClick={() => navigate('/business-documents/new')}
                >
                  Add Document
                </Button>
              )
            }
          />
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredDocuments.map((document) => (
              <DocumentCard
                key={document.id}
                document={document}
                onEdit={() => navigate(`/business-documents/${document.id}/edit`)}
                onDelete={() => {
                  if (window.confirm('Are you sure you want to delete this document?')) {
                    deleteMutation.mutate(document.id);
                  }
                }}
                isExpiringSoon={isExpiringSoon(document.expirationDate)}
                isExpired={isExpired(document.expirationDate)}
                getStatusVariant={getStatusVariant}
              />
            ))}
          </div>
        )}
      </Card>

    </div>
  );
};

interface DocumentCardProps {
  document: BusinessDocument;
  onEdit: () => void;
  onDelete: () => void;
  isExpiringSoon: boolean;
  isExpired: boolean;
  getStatusVariant: (status: BusinessDocumentStatus) => "success" | "warning" | "danger" | "gray";
}

const DocumentCard: React.FC<DocumentCardProps> = ({
  document,
  onEdit,
  onDelete,
  isExpiringSoon,
  isExpired,
  getStatusVariant,
}) => {
  return (
    <div className="px-6 py-4 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-transparent transition-all">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center flex-shrink-0">
            <FileText className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h4 className="text-lg font-semibold text-gray-900">{document.name}</h4>
              <Badge variant={getStatusVariant(document.status)} size="sm">
                {BusinessDocumentStatusLabels[document.status]}
              </Badge>
              {isExpiringSoon && (
                <Badge variant="warning" size="sm">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Expiring Soon
                </Badge>
              )}
              {isExpired && (
                <Badge variant="danger" size="sm">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Expired
                </Badge>
              )}
              <Badge variant="info" size="sm">
                {BusinessDocumentTypeLabels[document.type]}
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-2">
              {document.documentNumber && (
                <span className="font-medium">#{document.documentNumber}</span>
              )}
              {document.issuingAuthority && (
                <span>Issued by {document.issuingAuthority}</span>
              )}
              {document.issueDate && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Issued: {format(document.issueDate, 'MMM d, yyyy')}
                </span>
              )}
              {document.expirationDate && (
                <span className={`flex items-center gap-1 ${isExpired ? 'text-red-600 font-semibold' : isExpiringSoon ? 'text-yellow-600 font-semibold' : ''}`}>
                  <Calendar className="w-4 h-4" />
                  Expires: {format(document.expirationDate, 'MMM d, yyyy')}
                </span>
              )}
            </div>
            {document.description && (
              <p className="text-sm text-gray-600 line-clamp-2 mb-2">{document.description}</p>
            )}
            {document.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {document.tags.map((tag) => (
                  <Badge key={tag} variant="gray" size="sm">{tag}</Badge>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-4 flex-shrink-0">
          {document.fileUrl && (
            <Button
              variant="outline"
              size="sm"
              icon={Download}
              onClick={(e) => {
                e.stopPropagation();
                window.open(document.fileUrl, '_blank');
              }}
            >
              Download
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            icon={Edit}
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            Edit
          </Button>
          <Button
            variant="danger"
            size="sm"
            icon={Trash2}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
};
